import { DEFAULT_COUNTRY, phoneCandidate, type Doctor } from '@/domain';
import type { PhoneNumbers } from '@/application/ports/PhoneBook';

/**
 * A telephone number as the form holds one: the country's code in a selector,
 * and the rest in a box. The request of 6 October 2026 (fifth), section 2.
 *
 * The telephone library arrives a moment after the form that needs it, and
 * the form has to work in that moment and must not change under the reader
 * when it comes. So everything here answers with or without the book: without
 * it a number is taken apart by the one thing every number the server sends
 * has, the space after its country code, and sent as it was typed for the
 * server to read; with it a number is formatted as it is typed, checked by its
 * country's rules, and sent as E.164.
 */

/** What is dialled for the country a new number starts in, the lab's own: `DEFAULT_COUNTRY`. */
export const DEFAULT_CALLING_CODE = '971';

export interface PhoneEntry {
  /**
   * The country the selector is on, as its ISO code, or null while only its
   * calling code is known: a number read before the library was there to say
   * which of the countries that share the code it is in.
   */
  country: string | null;
  /** The digits dialled for the country, without the plus: `971`. */
  code: string;
  /** The number without its country code, as the box holds it. */
  national: string;
}

/** No number: the lab's own country code, and an empty box. */
export const EMPTY_PHONE: PhoneEntry = {
  country: DEFAULT_COUNTRY,
  code: DEFAULT_CALLING_CODE,
  national: '',
};

/** `+971 50 123 4567` taken apart at the space after its code, which needs no library. */
function fromDisplay(display: string | null): PhoneEntry | null {
  const [, code, rest] = /^\+(\d{1,3})[\s-]+(\S.*)$/.exec((display ?? '').trim()) ?? [];
  return code === undefined || rest === undefined ? null : { country: null, code, national: rest };
}

/** A number the library read, as the selector and the box hold it. */
function entryOf(split: { country: string; national: string }, book: PhoneNumbers): PhoneEntry {
  return {
    country: split.country,
    code: book.callingCode(split.country) ?? DEFAULT_CALLING_CODE,
    national: split.national,
  };
}

/**
 * The number a record has, as the form shows it: the country its code is, and
 * the rest as that country writes a number. Before the library is there, the
 * code and the rest as the server spells them. No number is the empty field.
 */
export function recordPhone(
  doctor: Pick<Doctor, 'phone' | 'phoneDisplay'> | undefined,
  book: PhoneNumbers | undefined,
): PhoneEntry {
  if (doctor === undefined || doctor.phone === null) return EMPTY_PHONE;

  const split = book?.split(doctor.phone) ?? null;
  if (book !== undefined && split !== null) return entryOf(split, book);
  // A number that will not come apart is shown whole, with its plus, and is read whole.
  return fromDisplay(doctor.phoneDisplay) ?? { ...EMPTY_PHONE, national: doctor.phone };
}

/**
 * What an edit starts from where the record has only the old free text: the
 * text read as a number, with the lab's own country assumed for one written
 * without a code. Unsaved: it is in the box for the person to check. Null when
 * the record has a number already, has no old text, the text does not read as
 * a number, or the library is not there yet to read it.
 */
export function oldTextPhone(
  doctor: Pick<Doctor, 'phone' | 'mobile'> | undefined,
  book: PhoneNumbers | undefined,
): PhoneEntry | null {
  if (doctor === undefined || book === undefined) return null;
  if (doctor.phone !== null || doctor.mobile === null) return null;

  const split = book.split(doctor.mobile, DEFAULT_COUNTRY);
  return split === null ? null : entryOf(split, book);
}

/**
 * The country a number is being typed for. The selector's own once somebody
 * has chosen one; for a number read before the library came, the country the
 * whole number is in; failing that, the first country that dials the code.
 */
export function countryOfEntry(entry: PhoneEntry, book: PhoneNumbers): string {
  if (entry.country !== null) return entry.country;

  const whole = book.split(`+${entry.code}${entry.national.replace(/\D/g, '')}`);
  if (whole !== null) return whole.country;
  return book.countries().find((each) => each.callingCode === entry.code)?.code ?? DEFAULT_COUNTRY;
}

/** What the library makes of what is in the box, or null without the library or with an empty box. */
export function phoneTyped(entry: PhoneEntry, book: PhoneNumbers | undefined) {
  if (book === undefined || entry.national.trim() === '') return null;
  return book.typed(entry.national, countryOfEntry(entry, book));
}

/**
 * What is sent for the number: null for an empty box, which clears it; the
 * E.164 for a number that is valid in the country chosen; and otherwise the
 * code and what was typed, which the form's own check and then the server
 * refuse in the server's words. A number typed whole, with a plus and its own
 * country code, is read as it stands whatever the selector says.
 */
export function phoneSent(entry: PhoneEntry, book: PhoneNumbers | undefined): string | null {
  const text = entry.national.trim();
  if (text === '') return null;

  if (text.startsWith('+')) {
    const candidate = phoneCandidate(text);
    return book?.read(candidate)?.e164 ?? candidate;
  }
  return phoneTyped(entry, book)?.e164 ?? `+${entry.code}${phoneCandidate(text)}`;
}

/** Whether two entries are the same number as typed: the same code, and the same digits after it. */
export function samePhone(a: PhoneEntry, b: PhoneEntry): boolean {
  const digits = (entry: PhoneEntry) => entry.national.replace(/[^\d+]/g, '');
  return a.code === b.code && digits(a) === digits(b);
}

/**
 * Where the caret goes in a number that was just formatted again: after as
 * many digits as were in front of it before, so typing in the middle of a
 * number does not throw the caret to its end.
 */
export function caretAfterFormat(typed: string, caret: number, formatted: string): number {
  if (caret >= typed.length) return formatted.length;

  const wanted = typed.slice(0, caret).replace(/[^\d+]/g, '').length;
  if (wanted === 0) return 0;

  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (/[\d+]/.test(formatted.charAt(index))) seen += 1;
    if (seen === wanted) return index + 1;
  }
  return formatted.length;
}
